import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

/** Connection state for the trading socket (SPEC §7.1, §17.2 status bar). */
export type ConnectionStatus = "disconnected" | "connecting" | "connected";

export type ConnectionState = {
  status: ConnectionStatus;
  /** Browser ↔ gateway round trip (ms) from the last Ping/Pong. */
  gwRttMs: number | null;
  /** Gateway ↔ Rithmic round trip (ms), reported by the gateway in Pong. 0 until Phase 1. */
  rithmicRttMs: number | null;
  serverMode: string | null;
  gatewayVersion: string | null;
  sessionId: string | null;
  /** `Welcome.boot_id` of the gateway process we last talked to (CB6: a change = restart). */
  bootId: string | null;
  /** Highest ordered-event seq received; resumption uses the contiguous appliedSeq instead. */
  lastSeq: bigint;
  /** Ordered cursor AFTER stream listeners apply the frame, separate from received lastSeq. */
  appliedSeq: bigint;
  /** Display only: Welcome replay target has been applied. Never an order gate. */
  financeReady: boolean;
  reconnectAttempt: number;
  lastError: string | null;
  set: (patch: Partial<Omit<ConnectionState, "set">>) => void;
};

export const useConnection = create<ConnectionState>((set) => ({
  status: "disconnected",
  gwRttMs: null,
  rithmicRttMs: null,
  serverMode: null,
  gatewayVersion: null,
  sessionId: null,
  bootId: null,
  lastSeq: 0n,
  appliedSeq: 0n,
  financeReady: false,
  reconnectAttempt: 0,
  lastError: null,
  set: (patch) => {
    set(
      patch.status !== undefined && patch.status !== "connected" ? { ...patch, financeReady: false } : patch,
    );
  },
}));

/**
 * What the UI shows of the connection: everything but `lastSeq` (and the setter). `lastSeq` moves
 * with every sequenced trading frame — each RiskState, many a second with a position open — and is
 * read only on reconnect; a shell subscribed to it re-rendered the whole app per frame.
 */
export type ConnectionView = Omit<ConnectionState, "lastSeq" | "set" | "appliedSeq" | "financeReady">;

export function connectionViewOf(s: ConnectionState): ConnectionView {
  return {
    status: s.status,
    gwRttMs: s.gwRttMs,
    rithmicRttMs: s.rithmicRttMs,
    serverMode: s.serverMode,
    gatewayVersion: s.gatewayVersion,
    sessionId: s.sessionId,
    bootId: s.bootId,
    reconnectAttempt: s.reconnectAttempt,
    lastError: s.lastError,
  };
}

/** The connection view; re-renders only when a shown field changes (never for `lastSeq`). */
export function useConnectionView(): ConnectionView {
  return useConnection(useShallow(connectionViewOf));
}
